import React, { useState, useRef, useEffect } from 'react';
import type { ChatMessage as BaseChatMessage, MediaPart } from '../types';
import { analyzeImage } from '../services/geminiService';
import { SendIcon, LogoIcon, UploadIcon } from '../components/icons';
import ChatMessageComponent from '../components/ChatMessage';

// The ChatMessageComponent expects content to be string | ToolResult,
// but for this simple chat, it will only ever be a string.
type ChatMessage = Omit<BaseChatMessage, 'content'> & { content: string };


const fileToBase64 = (file: File): Promise<{ data: string, mimeType: string }> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
            const result = reader.result as string;
            // Format: "data:image/jpeg;base64,..." -> "..."
            const data = result.split(',')[1];
            const mimeTypeMatch = result.match(/:(.*?);/);
            if (!mimeTypeMatch || !mimeTypeMatch[1]) {
                return reject(new Error('Could not determine MIME type from file.'));
            }
            const mimeType = mimeTypeMatch[1];
            resolve({ data, mimeType });
        };
        reader.onerror = error => reject(error);
    });
};


const ImageAnalysisView: React.FC = () => {
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [uploadedImage, setUploadedImage] = useState<MediaPart | null>(null);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const chatEndRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            try {
                setError(null);
                const { data, mimeType } = await fileToBase64(file);
                setUploadedImage({ inlineData: { data, mimeType } });
                setImagePreview(URL.createObjectURL(file));
                // Clear previous chat on new image upload
                setMessages([]);
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to process image.');
                setUploadedImage(null);
                setImagePreview(null);
            }
        }
    };

    const handleSend = async () => {
        if (!input.trim() || isLoading || !uploadedImage) return;
        
        const userMessage: ChatMessage = { id: Date.now().toString(), role: 'user', content: input };
        setMessages(prev => [...prev, userMessage]);
        const textPart = { text: input };
        setInput('');
        setIsLoading(true);
        setError(null);

        try {
            const modelResponseContent = await analyzeImage({ parts: [uploadedImage, textPart] });
            const modelMessage: ChatMessage = { id: (Date.now() + 1).toString(), role: 'model', content: modelResponseContent };
            setMessages(prev => [...prev, modelMessage]);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : "Sorry, an error occurred.";
            const modelMessage: ChatMessage = { id: (Date.now() + 1).toString(), role: 'model', content: errorMessage };
            setMessages(prev => [...prev, modelMessage]);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="p-4 md:p-6 max-w-4xl mx-auto h-full flex flex-col">
            {!uploadedImage ? (
                <div className="flex-1 flex flex-col justify-center items-center text-center">
                    <h2 className="text-2xl font-bold">Image Analysis</h2>
                    <p className="text-text-secondary mt-2 mb-6">Upload an image to start a conversation about it.</p>
                    <button
                        onClick={() => fileInputRef.current?.click()}
                        className="flex items-center gap-2 bg-brand-primary hover:bg-brand-secondary text-white font-bold py-3 px-6 rounded-lg transition-colors"
                    >
                        <UploadIcon className="w-5 h-5" />
                        Upload Image
                    </button>
                    <input type="file" ref={fileInputRef} onChange={handleImageUpload} accept="image/png, image/jpeg, image/webp" className="hidden" />
                    {error && <p className="text-red-400 mt-4">{error}</p>}
                </div>
            ) : (
                <>
                <div className="flex-shrink-0 mb-4 p-2 bg-base-200 rounded-lg border border-base-300 max-w-sm mx-auto">
                    <img src={imagePreview} alt="Analysis subject" className="rounded-md max-w-full h-auto object-contain max-h-64"/>
                </div>
                 <div className="flex-1 overflow-y-auto pr-2">
                    {messages.map(msg => <ChatMessageComponent key={msg.id} message={msg as BaseChatMessage} />)}
                     {isLoading && (
                        <div className="flex items-start gap-4 my-4">
                            <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-brand-primary">
                                <LogoIcon className="w-6 h-6 text-white animate-pulse" />
                            </div>
                            <div className="max-w-2xl p-4 rounded-lg bg-base-200">
                                <div className="flex items-center gap-2">
                                    <div className="w-2 h-2 bg-text-secondary rounded-full animate-bounce"></div>
                                    <div className="w-2 h-2 bg-text-secondary rounded-full animate-bounce [animation-delay:0.2s]"></div>
                                    <div className="w-2 h-2 bg-text-secondary rounded-full animate-bounce [animation-delay:0.4s]"></div>
                                </div>
                            </div>
                        </div>
                    )}
                    <div ref={chatEndRef} />
                 </div>
                 <div className="mt-4 flex-shrink-0">
                    <div className="flex items-center bg-base-200 rounded-lg p-2 border border-base-300 focus-within:border-brand-primary">
                        <input
                            type="text"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSend(); }}}
                            placeholder="Ask a question about the image..."
                            className="flex-1 bg-transparent focus:outline-none p-2"
                        />
                        <button
                            onClick={handleSend}
                            disabled={isLoading || !input.trim()}
                            className="p-2 rounded-md bg-brand-primary text-white disabled:bg-base-300"
                            aria-label="Send message"
                        >
                            <SendIcon className="w-5 h-5" />
                        </button>
                    </div>
                    <button
                         onClick={() => { setUploadedImage(null); setImagePreview(null); setMessages([]); }}
                         className="text-xs text-text-secondary hover:text-text-primary mt-2 underline"
                    >
                         Upload a different image
                    </button>
                 </div>
                </>
            )}
        </div>
    );
};

export default ImageAnalysisView;